/* eslint-disable no-restricted-imports */
/**
 * Utils to format the date.
 */
import type { OptionsWithTZ } from 'date-fns-tz';
import { formatInTimeZone } from 'date-fns-tz';
import { formatRelative as formatRelativeFns } from 'date-fns';

import { parseDate } from './parseDate';
import { getDateFeatureConfig } from './DateConfigType';
import { DateFormat } from './dateFormats';
import { getCurrentLocale } from './locale';

export { formatDuration, getQuarter } from 'date-fns';
export { formatInTimeZone } from 'date-fns-tz';

type DateFormatInputType = number | string | Date | null | undefined;
type ExtendedOptionsWithTZ = OptionsWithTZ & {
    useAdditionalWeekYearTokens?: boolean;
    useAdditionalDayOfYearTokens?: boolean;
};

function getAsDateObject(date: DateFormatInputType): Date | undefined {
    if (date instanceof Date) {
        return date;
    }

    return parseDate(date);
}

const DEFAULT_TIMEZONE = 'Europe/Vienna';

/**
 * Formats the date, provides optional format config override.
 *
 * Returns "now" if undefined is given.
 *
 * @param date date type - number/string/Date/undefined.
 * @param formatConfigOverride unicode token format override
 * @param locale locale object
 */
export function formatDate(
    date: DateFormatInputType,
    formatConfigOverride: DateFormat = getDateFeatureConfig(
        (f) => f?.base?.defaultDateFormat,
    ) as DateFormat,
    locale: Locale | undefined = getCurrentLocale(),
): string {
    if (
        typeof date !== 'number' &&
        typeof date !== 'string' &&
        !(date instanceof Date) &&
        date !== undefined
    ) {
        return 'Invalid date';
    }

    const parsedDate = getAsDateObject(date);

    const formatConfig = formatConfigOverride ?? DateFormat.DATE_MONTH_YEAR_DOTS_WITH_TIME;
    const timezone = getDateFeatureConfig((f) => f?.base?.defaultTimezone) ?? DEFAULT_TIMEZONE;

    return formatInTimeZone(parsedDate ?? new Date(), timezone, formatConfig, {
        useAdditionalDayOfYearTokens: true,
        locale,
    } as ExtendedOptionsWithTZ);
}

/**
 * Formats the date from defined format to output format.
 *
 * @param date - date string
 * @param inputFormat - input format as unicode tokens
 * @param outputFormat - optional output format as unicode tokens (fallbacks to defaultDateFormat)
 * @return {string} formatted date using output format
 */
export function formatDateWithInputFormat(
    date: string,
    inputFormat: DateFormat,
    outputFormat?: DateFormat,
): string {
    const parsedDate = parseDate(date, inputFormat, true) || '';

    if (!parsedDate) {
        return 'Invalid date';
    }

    return formatDate(parsedDate, outputFormat);
}

/**
 * Formats date to the DateFormat.API_DATE_WITH_OFFSET format. Used by API.
 * @param date date to format
 * @return formatted date
 */
export function formatDateWithOffset(date: Date): string {
    return formatInTimeZone(
        date,
        getDateFeatureConfig((f) => f?.base?.defaultTimezone) as string,
        DateFormat.API_DATE_WITH_OFFSET,
    );
}

/**
 * Represent the date in words relative to the given base date.
 * See: https://date-fns.org/v2.29.3/docs/formatRelative
 *
 * @param date the date to format
 * @param locale locale object
 * @param baseDate the date to compare with
 */
export function formatRelative(date: number | Date, baseDate = new Date()): string {
    const locale = getCurrentLocale();
    const options = locale ? { locale } : undefined;
    const relativeDate = formatRelativeFns(date, baseDate, options);

    return relativeDate.charAt(0).toUpperCase() + relativeDate.slice(1);
}

const PAD_ZEROES_LEN = 2;
const SECS_IN_MIN = 60;
/**
 * Represent number in seconds in mm:ss format
 *
 * @param seconds seconds to format
 */
export function formatSecondsToMinutesAndSeconds(seconds: number): string {
    const minutesInSeconds = Math.floor(seconds / SECS_IN_MIN);
    const remainingSeconds = seconds % SECS_IN_MIN;

    return `${String(minutesInSeconds).padStart(PAD_ZEROES_LEN, '0')}:${String(
        remainingSeconds,
    ).padStart(PAD_ZEROES_LEN, '0')}`;
}

/**
 * Formats date with current timezone of client's browser.
 * This effectively acts as if timezone was ignored during formatting,
 * opposed to {@link formatDate} where date is converted
 * to the bank's default timezone and dates might get shifted.
 */
export function formatDateInClientTimeZone(
    date: Date | number | string,
    format: DateFormat,
    locale: Locale | undefined = getCurrentLocale(),
): string {
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatInTimeZone(date, clientTimeZone, format, {
        useAdditionalDayOfYearTokens: true,
        locale,
    } as ExtendedOptionsWithTZ);
}

/**
 * Formats date with UTC timezone.
 * This will always output a string that is ending with 'Z', referring to UTC timezone.
 *
 * @param date - string/Date
 */
export function formatDateInUtcISO(date: Date | string): string {
    return formatInTimeZone(date, 'UTC', DateFormat.UTC_ISO_DATE_FORMAT);
}
